import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { usePilotMode } from "@/hooks/use-pilot-mode";
import { Loader2, ArrowRight, ArrowLeft, Building2, Globe, Users, Palette, Target, Sparkles, CheckCircle2, Linkedin, SlidersHorizontal, MapPin, Image, ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import { SiX, SiMeta } from "react-icons/si";
import { BrandKitPuller, isBareDomainUrl } from "@/components/brand-kit-puller";

const STEPS = [
  { title: "Your Business", icon: Building2, description: "Tell us about your business" },
  { title: "Brand & Audience", icon: Palette, description: "Define your brand identity" },
  { title: "Marketing Channels", icon: Globe, description: "Select your marketing channels" },
  { title: "Marketing Goals", icon: Target, description: "What do you want to achieve?" },
  { title: "Preferences", icon: SlidersHorizontal, description: "Set your content preferences" },
  { title: "Your Playbook", icon: BookOpen, description: "Your 30-day content plan" },
  { title: "Launch", icon: Sparkles, description: "Let's get started" },
];

const PLAYBOOK_INDUSTRIES = [
  "Accounting / CPA", "Dental", "Legal", "Real Estate", "Restaurant",
  "Retail / Boutique", "Bakery", "Fitness / Gym", "Salon / Spa",
  "Construction / Trades", "Tech / SaaS", "Consulting", "Healthcare / Clinic",
  "Auto / Dealership", "E-commerce", "Other",
];

const INDUSTRIES = [
  "SaaS / Software", "E-commerce", "Fintech", "Healthcare", "Education",
  "Real Estate", "Marketing Agency", "Consulting", "Retail", "Food & Beverage",
  "Fitness & Wellness", "Legal", "Insurance", "Automotive",
  "Home Services", "Construction & Trades", "Professional Services",
  "Hospitality", "Non-Profit", "Health & Beauty", "Other",
];

const GOALS = [
  "Grow social media following",
  "Generate more leads",
  "Increase brand awareness",
  "Drive website traffic",
  "Launch a new product",
  "Build thought leadership",
  "Improve customer engagement",
  "Scale advertising campaigns",
];

const VOICES = ["Professional", "Casual", "Bold", "Friendly", "Technical", "Luxury"];

const CHANNELS = [
  { key: "linkedin" as const, label: "LinkedIn", icon: Linkedin, color: "blue" },
  { key: "x" as const, label: "X (Twitter)", icon: SiX, color: "white" },
  { key: "meta" as const, label: "Meta (Facebook)", icon: SiMeta, color: "blue" },
  { key: "google" as const, label: "Google", icon: Globe, color: "emerald" },
];

const POSTING_FREQUENCIES = ["Daily", "3x/week", "5x/week", "Weekly"];

const CONTENT_STYLES = [
  "Educational", "Promotional", "Behind-the-scenes", "Thought Leadership",
  "Entertainment", "Case Studies", "User Stories", "Industry News",
];

const PERSONALITIES = [
  "Professional", "Casual & Fun", "Bold & Edgy", "Warm & Friendly", "Data-Driven", "Inspirational",
];

const CHANNEL_RECOMMENDATIONS: Record<string, { platform: string; priority: "high" | "medium" | "low" }[]> = {
  "SaaS / Software": [
    { platform: "linkedin", priority: "high" },
    { platform: "x", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "low" },
  ],
  "E-commerce": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Fintech": [
    { platform: "linkedin", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "x", priority: "medium" },
    { platform: "meta", priority: "medium" },
  ],
  "Healthcare": [
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "high" },
    { platform: "linkedin", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Education": [
    { platform: "linkedin", priority: "high" },
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Real Estate": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Marketing Agency": [
    { platform: "linkedin", priority: "high" },
    { platform: "meta", priority: "high" },
    { platform: "x", priority: "high" },
    { platform: "google", priority: "medium" },
  ],
  "Consulting": [
    { platform: "linkedin", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "medium" },
    { platform: "x", priority: "medium" },
  ],
  "Retail": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Food & Beverage": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Fitness & Wellness": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Legal": [
    { platform: "linkedin", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Insurance": [
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "high" },
    { platform: "meta", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Automotive": [
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Home Services": [
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Construction & Trades": [
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Professional Services": [
    { platform: "linkedin", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "meta", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
  "Hospitality": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Non-Profit": [
    { platform: "meta", priority: "high" },
    { platform: "linkedin", priority: "high" },
    { platform: "google", priority: "medium" },
    { platform: "x", priority: "medium" },
  ],
  "Health & Beauty": [
    { platform: "meta", priority: "high" },
    { platform: "google", priority: "high" },
    { platform: "linkedin", priority: "low" },
    { platform: "x", priority: "low" },
  ],
  "Other": [
    { platform: "meta", priority: "medium" },
    { platform: "google", priority: "medium" },
    { platform: "linkedin", priority: "medium" },
    { platform: "x", priority: "low" },
  ],
};

type DeployPhase = "idle" | "creating" | "branding" | "analyzing" | "calendar" | "done";

interface BrandAssets {
  logoUrl: string | null;
  brandColors: string[];
}

export default function Onboarding({ onComplete }: { onComplete: () => void }) {
  const { toast } = useToast();
  const { setActiveWorkspaceId } = useWorkspaceStore();
  const pilotMode = usePilotMode(); // PILOT_MODE: no "View Plans & Upgrade" after deploy
  const [step, setStep] = useState(0);
  const [deployPhase, setDeployPhase] = useState<DeployPhase>("idle");
  const [brandAssets, setBrandAssets] = useState<BrandAssets | null>(null);
  const [form, setForm] = useState({
    name: "",
    url: "",
    description: "",
    phone: "",
    email: "",
    city: "",
    address: "",
    industry: "",
    targetAudience: "",
    brandVoice: "Professional",
    brandTone: "",
    goals: [] as string[],
    channels: { linkedin: true, x: true, meta: true, google: true },
    postingFrequency: "3x/week",
    contentStyles: ["Educational", "Promotional"] as string[],
    personality: "Professional",
    autoApprove: false,
  });

  const [deployWarnings, setDeployWarnings] = useState<string[]>([]);
  const [showManualBrand, setShowManualBrand] = useState(false);
  const [urlScraping, setUrlScraping] = useState(false);
  const [urlAutoFilled, setUrlAutoFilled] = useState<string[]>([]);
  const lastScrapedUrlRef = useRef("");
  const [manualBrand, setManualBrand] = useState({ logoUrl: "", primaryColor: "" });

  // Brand Kit (T008)
  const [showBrandKit, setShowBrandKit] = useState(false);
  const [brandKit, setBrandKit] = useState({
    primaryColor: "#2563eb",
    secondaryColor: "#1e40af",
    accentColor: "#f59e0b",
    fontPreference: "modern",
    photoStyle: "real photography",
    tagline: "",
  });

  // Playbook (T009)
  const [playbookIndustry, setPlaybookIndustry] = useState("");
  const [playbookOtherIndustry, setPlaybookOtherIndustry] = useState("");
  const [playbookFreq, setPlaybookFreq] = useState("3-5x/week");
  const [playbookGoals, setPlaybookGoals] = useState<string[]>(["Grow brand awareness", "Generate leads"]);
  const [playbookAction, setPlaybookAction] = useState<"none" | "template" | "generate">("none");
  const [playbookResult, setPlaybookResult] = useState<any>(null);
  const [playbookLoading, setPlaybookLoading] = useState(false);
  const [playbookPendingWorkspaceId, setPlaybookPendingWorkspaceId] = useState<number | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);

  const { data: packages } = useQuery<any[]>({
    queryKey: ["/api/saas-packages"],
    enabled: showPricing,
  });

  const INDUSTRY_TO_PLAYBOOK: Record<string, string> = {
    "Automotive": "Auto / Dealership",
    "Restaurant": "Restaurant",
    "Food & Beverage": "Restaurant",
    "Real Estate": "Real Estate",
    "Legal": "Legal",
    "Healthcare": "Healthcare / Clinic",
    "Fitness & Wellness": "Fitness / Gym",
    "Health & Beauty": "Salon / Spa",
    "Construction & Trades": "Construction / Trades",
    "SaaS / Software": "Tech / SaaS",
    "Consulting": "Consulting",
    "Retail": "Retail / Boutique",
    "E-commerce": "E-commerce",
    "Education": "Consulting",
    "Insurance": "Accounting / CPA",
    "Home Services": "Construction / Trades",
  };

  useEffect(() => {
    if (form.industry) {
      const recommendations = CHANNEL_RECOMMENDATIONS[form.industry];
      if (recommendations) {
        const newChannels = { ...form.channels };
        recommendations.forEach(rec => {
          const channelKey = rec.platform as keyof typeof form.channels;
          if (rec.priority === "high") {
            newChannels[channelKey] = true;
          }
        });
        setForm(prev => ({ ...prev, channels: newChannels }));
      }
      const mapped = INDUSTRY_TO_PLAYBOOK[form.industry];
      if (mapped && !playbookIndustry) {
        setPlaybookIndustry(mapped);
      }
    }
  }, [form.industry]);

  const handleUrlBlur = useCallback(async () => {
    const rawUrl = form.url.trim();
    if (!rawUrl || rawUrl.length < 5 || rawUrl === lastScrapedUrlRef.current) return;
    lastScrapedUrlRef.current = rawUrl;
    setUrlScraping(true);
    try {
      const res = await apiRequest("POST", "/api/ai/scrape-business-info", { url: rawUrl });
      const data = await res.json();
      const filled: string[] = [];
      setForm(prev => {
        const next = { ...prev };
        if (data.businessName && !prev.name.trim()) { next.name = data.businessName; filled.push("name"); }
        if (data.industry && INDUSTRIES.includes(data.industry) && !prev.industry) { next.industry = data.industry; filled.push("industry"); }
        if (data.targetAudience && !prev.targetAudience.trim()) { next.targetAudience = data.targetAudience; filled.push("targetAudience"); }
        if (data.brandVoice && ["Professional","Casual","Bold","Friendly","Technical","Luxury"].includes(data.brandVoice) && prev.brandVoice === "Professional") { next.brandVoice = data.brandVoice; filled.push("brandVoice"); }
        if (data.description && !prev.description.trim()) { next.description = data.description; filled.push("description"); }
        if (data.city && !prev.city.trim()) { next.city = data.city; filled.push("city"); }
        return next;
      });
      if (data.industry && INDUSTRIES.includes(data.industry)) {
        const mapped = INDUSTRY_TO_PLAYBOOK[data.industry];
        if (mapped && !playbookIndustry) setPlaybookIndustry(mapped);
      }
      setUrlAutoFilled(filled);
    } catch { /* non-fatal — user can fill manually */ }
    finally { setUrlScraping(false); }
  }, [form.url, form.name, form.industry, form.targetAudience, form.brandVoice, form.description, form.city, playbookIndustry]);

  const deployMutation = useMutation({
    mutationFn: async () => {
      const warnings: string[] = [];
      setDeployWarnings([]);

      setDeployPhase("creating");
      // Build contactInfo from city + optional address fields
      const contactInfoPayload: Record<string, string> = {};
      if (form.phone.trim()) contactInfoPayload.phone = form.phone.trim();
      if (form.email.trim()) contactInfoPayload.email = form.email.trim();
      if (form.url.trim()) contactInfoPayload.website = form.url.trim();
      if (form.city.trim()) contactInfoPayload.city = form.city.trim();
      if (form.address.trim()) contactInfoPayload.address = [form.address.trim(), form.city.trim()].filter(Boolean).join(", ");

      const wsRes = await apiRequest("POST", "/api/workspaces", {
        name: form.name,
        url: form.url,
        description: form.description.trim() || undefined,
        industry: form.industry,
        targetAudience: form.targetAudience,
        brandVoice: form.brandVoice,
        brandTone: form.brandTone,
        channelPreferences: form.channels,
        postingPreferences: {
          frequency: form.postingFrequency,
          contentStyles: form.contentStyles,
          personality: form.personality,
          autoApprove: form.autoApprove,
        },
        marketingGoals: form.goals,
        contactInfo: Object.keys(contactInfoPayload).length > 0 ? contactInfoPayload : undefined,
        ...(manualBrand.logoUrl.trim() ? { logoUrl: manualBrand.logoUrl.trim() } : {}),
        ...(manualBrand.primaryColor.trim() ? { brandColors: [manualBrand.primaryColor.trim()] } : {}),
      });
      const workspace = await wsRes.json();
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });

      if (form.url && form.url.trim()) {
        setDeployPhase("branding");
        try {
          const brandRes = await apiRequest("POST", `/api/workspaces/${workspace.id}/extract-brand`);
          const brandResult = await brandRes.json();
          if (brandResult && (brandResult.logoUrl || (brandResult.brandColors && brandResult.brandColors.length > 0))) {
            setBrandAssets(brandResult);
            const brandUpdate: Record<string, any> = {};
            if (brandResult.logoUrl) brandUpdate.logoUrl = brandResult.logoUrl;
            if (brandResult.brandColors && brandResult.brandColors.length > 0) brandUpdate.brandColors = brandResult.brandColors;
            await apiRequest("PATCH", `/api/workspaces/${workspace.id}`, brandUpdate);
          }
        } catch (e: any) {
          console.error("Brand extraction failed:", e);
          warnings.push("Brand asset extraction could not complete. You can extract brand assets later from Settings.");
        }
      }

      setDeployPhase("analyzing");
      let analysisResult: any = null;
      try {
        const analyzeRes = await apiRequest("POST", "/api/ai/analyze", {
          url: form.url || form.name,
          businessName: form.name,
          workspaceId: workspace.id,
        });
        analysisResult = await analyzeRes.json();
      } catch (e: any) {
        console.error("Analysis failed:", e);
        warnings.push("AI analysis could not complete. You can run it later from the Strategy page.");
      }

      if (analysisResult) {
        try {
          await apiRequest("POST", `/api/workspaces/${workspace.id}/plans`, {
            workspaceId: workspace.id,
            summary: analysisResult.summary || "",
            competitors: analysisResult.competitors || [],
            swot: analysisResult.swot || { strengths: [], weaknesses: [], opportunities: [], threats: [] },
            strategy: analysisResult.strategy || analysisResult.draftStrategy || "",
            structuredStrategy: analysisResult.structuredStrategy || null,
            status: "ACTIVE",
          });
        } catch (e: any) {
          console.error("Save plan failed:", e);
          warnings.push("Could not save the marketing plan.");
        }

        if (analysisResult.structuredStrategy && playbookAction === "none") {
          setDeployPhase("calendar");
          try {
            await apiRequest("POST", "/api/content/generate-calendar", {
              workspaceId: workspace.id,
            });
          } catch (e: any) {
            console.error("Calendar generation failed:", e);
            warnings.push("Content calendar generation had issues. You can regenerate it from the Content Studio.");
          }
        }
      }

      // Save brand kit if user filled it out
      if (showBrandKit && (brandKit.primaryColor !== "#2563eb" || manualBrand.logoUrl || brandKit.tagline)) {
        try {
          await apiRequest("PUT", `/api/workspaces/${workspace.id}/brand-kit`, {
            logoUrl: manualBrand.logoUrl || null,
            primaryColor: brandKit.primaryColor,
            secondaryColor: brandKit.secondaryColor,
            accentColor: brandKit.accentColor,
            fontPreference: brandKit.fontPreference,
            photoStyle: brandKit.photoStyle,
            tagline: brandKit.tagline || null,
            brandVoice: form.brandVoice,
          });
        } catch (e) {
          console.warn("Brand kit save failed:", e);
        }
      }

      // Save playbook based on what the user chose during onboarding
      if (playbookAction === "template" && (playbookIndustry || playbookOtherIndustry)) {
        try {
          const industryToUse = playbookIndustry === "Other" ? playbookOtherIndustry : playbookIndustry;
          await apiRequest("POST", `/api/workspaces/${workspace.id}/playbook/use-template`, { industry: industryToUse });
        } catch (e) {
          console.warn("Template apply failed:", e);
          warnings.push("Playbook template could not be applied. You can set it up later in Content Studio.");
        }
      } else if (playbookAction === "generate" && (playbookIndustry || playbookOtherIndustry)) {
        try {
          const industryToUse = playbookIndustry === "Other" ? playbookOtherIndustry : playbookIndustry;
          const channels = Object.entries(form.channels).filter(([, v]) => v).map(([k]) => k);
          await apiRequest("POST", `/api/workspaces/${workspace.id}/playbook/generate`, {
            industry: industryToUse,
            location: form.city,
            goals: playbookGoals,
            postingFrequency: playbookFreq,
            channels,
            isOnboarding: true,
          });
        } catch (e: any) {
          console.warn("Playbook generation failed:", e);
          // Fallback: if AI blocked (403), apply template instead
          if (e.status === 403 || e.message?.includes("budget")) {
            try {
              const industryToUse = playbookIndustry === "Other" ? playbookOtherIndustry : playbookIndustry;
              await apiRequest("POST", `/api/workspaces/${workspace.id}/playbook/use-template`, {
                industry: industryToUse,
              });
              warnings.push("Custom playbook queued for later — template applied for now.");
            } catch {
              warnings.push("Custom playbook generation had issues. You can generate it later in Content Studio.");
            }
          } else {
            warnings.push("Custom playbook generation had issues. You can generate it later in Content Studio.");
          }
        }
      }

      if (playbookAction !== "none") {
        setDeployPhase("calendar");
        try {
          await apiRequest("POST", `/api/workspaces/${workspace.id}/playbook/seed-content`);
        } catch (e: any) {
          console.warn("Playbook seed-content failed:", e);
          warnings.push("Initial playbook content seeding had issues. The daily scheduler will pick it up automatically.");
        }
      }

      setDeployWarnings(warnings);
      setDeployPhase("done");
      return workspace;
    },
    onSuccess: (workspace: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      if (workspace?.id) {
        setActiveWorkspaceId(workspace.id);
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "playbook"] });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "plans"] });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "content"] });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "campaigns"] });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "leads"] });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "agent-runs"] });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspace.id, "logs"] });

        apiRequest("POST", `/api/workspaces/${workspace.id}/orchestrate`, {
          businessName: workspace.brandName || workspace.name,
          url: workspace.url || "",
        }).catch(() => {
          toast({
            title: "Strategy Generation Queued",
            description: "Draper will build your full strategy on next run. Visit Strategy Hub to launch manually.",
            variant: "default",
          });
        });
      }
      if (deployWarnings.length > 0) {
        toast({ title: "Workspace Created", description: `Set up with ${deployWarnings.length} warning(s). Draper is now building your full strategy in the background.` });
      } else {
        toast({ title: "Welcome to Drpr.ai", description: "Your marketing command center is live. Draper is building your strategy now." });
      }
      setTimeout(() => onComplete(), 1500);
    },
    onError: (e: any) => {
      setDeployPhase("idle");
      toast({ title: "Error", description: e.message || "Failed to set up workspace.", variant: "destructive" });
    },
  });

  const toggleGoal = (goal: string) => {
    setForm((f) => ({
      ...f,
      goals: f.goals.includes(goal) ? f.goals.filter((g) => g !== goal) : [...f.goals, goal],
    }));
  };

  const toggleChannel = (key: "linkedin" | "x" | "meta" | "google") => {
    setForm((f) => ({
      ...f,
      channels: { ...f.channels, [key]: !f.channels[key] },
    }));
  };

  const toggleContentStyle = (style: string) => {
    setForm((f) => ({
      ...f,
      contentStyles: f.contentStyles.includes(style) ? f.contentStyles.filter((s) => s !== style) : [...f.contentStyles, style],
    }));
  };

  useEffect(() => {
    if (step === 1 && form.url && form.url.trim()) {
      setShowBrandKit(true);
    }
  }, [step]);

  const canAdvance = () => {
    if (step === 0) return form.name.trim().length > 0 || (form.url.trim().length > 4 && urlAutoFilled.length > 0);
    if (step === 1) return form.brandVoice.length > 0;
    if (step === 2) return Object.values(form.channels).some(Boolean);
    if (step === 3) return form.goals.length > 0;
    if (step === 4) return form.contentStyles.length > 0;
    if (step === 5) return true; // Playbook step is always optional
    return true;
  };

  const handlePlaybookTemplate = async () => {
    const industryToUse = playbookIndustry === "Other" ? playbookOtherIndustry : playbookIndustry;
    if (!industryToUse) {
      toast({ title: "Select an industry", description: "Please pick an industry before loading a template.", variant: "destructive" });
      return;
    }
    setPlaybookLoading(true);
    try {
      setPlaybookAction("template");
      toast({ title: "Template loaded", description: `30-day ${industryToUse} playbook is ready!` });
      setPlaybookResult({ industry: industryToUse });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setPlaybookLoading(false);
    }
  };

  const handlePlaybookGenerate = async (workspaceId?: number) => {
    const industryToUse = playbookIndustry === "Other" ? playbookOtherIndustry : playbookIndustry;
    if (!industryToUse) {
      toast({ title: "Select an industry", description: "Please pick an industry before generating.", variant: "destructive" });
      return;
    }
    if (!workspaceId) {
      toast({ title: "Workspace needed", description: "Please complete earlier steps first.", variant: "destructive" });
      return;
    }
    setPlaybookLoading(true);
    setPlaybookAction("generate");
    try {
      const channels = Object.entries(form.channels).filter(([, v]) => v).map(([k]) => k);
      const res = await apiRequest("POST", `/api/workspaces/${workspaceId}/playbook/generate`, {
        industry: industryToUse,
        location: form.city,
        goals: playbookGoals,
        postingFrequency: playbookFreq,
        channels,
      });
      const result = await res.json();
      setPlaybookResult(result);
      setPlaybookPendingWorkspaceId(workspaceId);
    } catch (e: any) {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
      setPlaybookAction("none");
    } finally {
      setPlaybookLoading(false);
    }
  };

  const deployPhaseLabels: Record<DeployPhase, string> = {
    idle: "",
    creating: "Setting up your workspace...",
    branding: "Extracting your brand logo and colors...",
    analyzing: "Draper is analyzing your business...",
    calendar: "Peggy is creating your content calendar...",
    done: "Your marketing command center is live!",
  };

  const selectedChannelLabels = Object.entries(form.channels)
    .filter(([, v]) => v)
    .map(([k]) => {
      const ch = CHANNELS.find((c) => c.key === k);
      return ch ? ch.label : k;
    });

  return (
    <div className="min-h-screen bg-background overflow-y-auto py-8 px-4 pb-16" data-testid="onboarding-page">
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-display font-black text-foreground mb-2">Welcome to Drpr.ai</h1>
          <p className="text-muted-foreground text-sm">Let's set up your marketing command center in a few steps.</p>
        </div>

        <div className="flex items-center justify-center gap-3 mb-10 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                  i < step ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                  : i === step ? "bg-blue-600 text-white shadow-lg shadow-blue-900/40"
                  : "bg-card/50 text-muted-foreground/50 border border-border"
                }`}
              >
                {i < step ? <CheckCircle2 size={18} /> : <s.icon size={18} />}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-px ${i < step ? "bg-emerald-500/40" : "bg-muted"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-8" data-testid="onboarding-form">
          <div className="mb-6">
            <h2 className="text-xl font-display font-bold text-foreground">{STEPS[step].title}</h2>
            <p className="text-muted-foreground/70 text-sm mt-1">{STEPS[step].description}</p>
          </div>

          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Business Name</label>
                <input
                  data-testid="input-business-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Acme Corp"
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Website URL <span className="text-muted-foreground/50 font-normal normal-case tracking-normal">(optional)</span></label>
                <div className="flex items-center gap-2">
                  {urlScraping ? <Loader2 size={16} className="animate-spin text-blue-400" /> : <Globe size={16} className="text-muted-foreground/50" />}
                  <input
                    data-testid="input-business-url"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    onBlur={handleUrlBlur}
                    placeholder="https://example.com"
                    className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground/50 mt-1.5">AI agents will visit your website to learn about your business. If your site is behind a login or can't be accessed, describe your business below.</p>
                {urlAutoFilled.length > 0 && !urlScraping && (
                  <p className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1" data-testid="url-autofill-notice">
                    <CheckCircle2 size={10} /> Auto-filled {urlAutoFilled.length} field{urlAutoFilled.length !== 1 ? 's' : ''} from your website
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">
                  Business Description
                  <span className="text-amber-400/70 font-normal normal-case tracking-normal ml-1">(recommended — used if your site can't be scraped)</span>
                </label>
                <textarea
                  data-testid="input-business-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe what your business does, who you serve, your key offerings, and what makes you unique. This ensures quality analysis if your website blocks automated access."
                  rows={3}
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none placeholder:text-muted-foreground/50"
                />
                <p className="text-[10px] text-muted-foreground/50 mt-1">If Draper can't read your website, this description is used to generate your strategy instead.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Phone <span className="text-muted-foreground/50 font-normal normal-case tracking-normal">(optional)</span></label>
                  <input
                    data-testid="input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="e.g. 416-555-9988"
                    className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Email <span className="text-muted-foreground/50 font-normal normal-case tracking-normal">(optional)</span></label>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="e.g. info@yourbusiness.com"
                    className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
              </div>
              <div className="bg-blue-600/10 border border-blue-500/30 rounded-xl p-3 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin size={14} className="text-blue-400" />
                  <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Business Location</span>
                </div>
                <p className="text-xs text-muted-foreground/70">Used for local competitor research and city-targeted strategy. The more specific, the better.</p>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">City &amp; Province / State <span className="text-blue-400 font-semibold normal-case tracking-normal">(recommended)</span></label>
                  <input
                    data-testid="input-business-city"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="e.g., Mississauga, ON"
                    className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Street Address <span className="text-muted-foreground/50 font-normal normal-case tracking-normal">(optional)</span></label>
                  <input
                    data-testid="input-business-address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="e.g., 5000 Yonge St, Unit 200"
                    className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Industry</label>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      data-testid={`industry-${ind.toLowerCase().replace(/[\s\/]/g, "-")}`}
                      onClick={() => setForm({ ...form, industry: ind })}
                      className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                        form.industry === ind
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Target Audience</label>
                <textarea
                  data-testid="input-audience"
                  value={form.targetAudience}
                  onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
                  placeholder="Describe your ideal customer... e.g., 'Small business owners aged 25-45 looking to scale their marketing'"
                  rows={3}
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none placeholder:text-muted-foreground/50"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand Voice</label>
                <div className="flex flex-wrap gap-2">
                  {VOICES.map((v) => (
                    <button
                      key={v}
                      data-testid={`voice-${v.toLowerCase()}`}
                      onClick={() => setForm({ ...form, brandVoice: v })}
                      className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                        form.brandVoice === v
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand Personality (Optional)</label>
                <textarea
                  data-testid="input-brand-tone"
                  value={form.brandTone}
                  onChange={(e) => setForm({ ...form, brandTone: e.target.value })}
                  placeholder="Describe your brand's personality... e.g., 'We're data-driven but approachable. We use insights with a conversational tone.'"
                  rows={2}
                  className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none placeholder:text-muted-foreground/50"
                />
              </div>

              {/* Brand Kit — Pull from website */}
              <div className="border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  data-testid="toggle-brand-kit"
                  onClick={() => setShowBrandKit(v => !v)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Image size={14} className="text-blue-400" />
                    <span className="text-sm font-bold text-foreground">Brand Kit</span>
                    <span className="text-xs text-muted-foreground/60">(optional)</span>
                  </div>
                  {showBrandKit ? <ChevronUp size={14} className="text-muted-foreground/50" /> : <ChevronDown size={14} className="text-muted-foreground/50" />}
                </button>
                {showBrandKit && (
                  <div className="p-4 space-y-4 bg-background/30" data-testid="brand-kit-section">
                    <p className="text-xs text-muted-foreground/70">Your agents use your brand assets to maintain visual consistency across all generated content and ads.</p>
                    <BrandKitPuller
                      workspaceId={null}
                      websiteUrl={form.url || ""}
                      extractUrl={form.url || ""}
                      autoExtract={!!form.url}
                      onApply={(data) => {
                        if (data.logoUrl) setManualBrand(b => ({ ...b, logoUrl: data.logoUrl! }));
                        if (data.brandColors && data.brandColors.length > 0) {
                          setBrandKit(k => ({
                            ...k,
                            primaryColor: data.brandColors![0] || k.primaryColor,
                            secondaryColor: data.brandColors![1] || k.secondaryColor,
                            accentColor: data.brandColors![2] || k.accentColor,
                          }));
                        }
                        if (data.tagline) setBrandKit(k => ({ ...k, tagline: data.tagline! }));
                      }}
                      onSkip={() => setShowBrandKit(false)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="channel-selection">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-4">Select Your Marketing Channels</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CHANNELS.map((ch) => {
                    const recommendations = form.industry ? CHANNEL_RECOMMENDATIONS[form.industry] : null;
                    const recommendation = recommendations?.find(r => r.platform === ch.key);
                    const priorityColors = {
                      high: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
                      medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
                      low: "bg-muted/30 text-muted-foreground/60 border-border",
                    };
                    return (
                      <button
                        key={ch.key}
                        type="button"
                        data-testid={`channel-${ch.key}`}
                        onClick={() => toggleChannel(ch.key)}
                        className={`flex items-center gap-4 px-5 py-4 rounded-xl text-left transition-all ${
                          form.channels[ch.key]
                            ? "bg-blue-600/10 border border-blue-500/30"
                            : "bg-muted/30 border border-border hover:border-border"
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          form.channels[ch.key] ? "bg-blue-600/20" : "bg-muted/50"
                        }`}>
                          <ch.icon size={20} className={form.channels[ch.key] ? "text-blue-400" : "text-muted-foreground/70"} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-bold block ${form.channels[ch.key] ? "text-foreground" : "text-muted-foreground"}`}>{ch.label}</span>
                            {form.channels[ch.key] && (
                              <span className="text-[10px] px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 font-medium" data-testid={`oauth-badge-${ch.key}`}>
                                Connect in Settings
                              </span>
                            )}
                          </div>
                          {recommendation && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-md border inline-block mt-1 ${priorityColors[recommendation.priority]}`}>
                              {recommendation.priority === "high" ? "★ " : ""}{recommendation.priority.toUpperCase()} for {form.industry}
                            </span>
                          )}
                        </div>
                        <div className={`w-10 h-6 rounded-full transition-colors relative ${
                          form.channels[ch.key] ? "bg-blue-600" : "bg-muted"
                        }`}>
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            form.channels[ch.key] ? "translate-x-5" : "translate-x-1"
                          }`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              {selectedChannelLabels.length > 0 && (
                <div className="bg-muted/30 border border-border rounded-xl p-4" data-testid="channel-confirmation">
                  <p className="text-xs text-muted-foreground mb-2">Your agents will create content for:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedChannelLabels.map((label) => (
                      <span
                        key={label}
                        data-testid={`channel-badge-${label.toLowerCase().replace(/[\s()]/g, "-")}`}
                        className="px-3 py-1 rounded-lg bg-blue-600/15 text-blue-400 text-xs font-medium border border-blue-500/20"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Marketing Goals (Select All That Apply)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {GOALS.map((goal) => (
                    <button
                      key={goal}
                      data-testid={`goal-${goal.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => toggleGoal(goal)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-left font-medium transition-all ${
                        form.goals.includes(goal)
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 ${
                        form.goals.includes(goal) ? "bg-blue-600 border-blue-500" : "border-border"
                      }`}>
                        {form.goals.includes(goal) && <CheckCircle2 size={10} className="text-foreground" />}
                      </div>
                      {goal}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6" data-testid="preferences-step">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-3">Posting Frequency</label>
                <div className="flex flex-wrap gap-2">
                  {POSTING_FREQUENCIES.map((freq) => (
                    <button
                      key={freq}
                      data-testid={`frequency-${freq.toLowerCase().replace(/\//g, "-")}`}
                      onClick={() => setForm({ ...form, postingFrequency: freq })}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        form.postingFrequency === freq
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {freq}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-3">Content Styles (Select Multiple)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {CONTENT_STYLES.map((style) => (
                    <button
                      key={style}
                      data-testid={`content-style-${style.toLowerCase().replace(/[\s-]/g, "-")}`}
                      onClick={() => toggleContentStyle(style)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-left font-medium transition-all ${
                        form.contentStyles.includes(style)
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 ${
                        form.contentStyles.includes(style) ? "bg-blue-600 border-blue-500" : "border-border"
                      }`}>
                        {form.contentStyles.includes(style) && <CheckCircle2 size={10} className="text-foreground" />}
                      </div>
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-3">Content Personality</label>
                <div className="flex flex-wrap gap-2">
                  {PERSONALITIES.map((p) => (
                    <button
                      key={p}
                      data-testid={`personality-${p.toLowerCase().replace(/[\s&]/g, "-")}`}
                      onClick={() => setForm({ ...form, personality: p })}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        form.personality === p
                          ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                          : "bg-muted/50 text-muted-foreground border border-border hover:text-foreground hover:border-border"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <button
                  data-testid="toggle-auto-approve"
                  onClick={() => setForm({ ...form, autoApprove: !form.autoApprove })}
                  className="flex items-center justify-between gap-4 w-full px-5 py-4 rounded-xl bg-muted/30 border border-border transition-all"
                >
                  <div className="text-left">
                    <span className="text-sm font-bold text-foreground block">Auto-Approve Content</span>
                    <span className="text-xs text-muted-foreground/70">Let agents auto-approve content</span>
                  </div>
                  <div className={`w-10 h-6 rounded-full transition-colors relative shrink-0 ${
                    form.autoApprove ? "bg-blue-600" : "bg-muted"
                  }`}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      form.autoApprove ? "translate-x-5" : "translate-x-1"
                    }`} />
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5" data-testid="playbook-step">
              <div className="bg-blue-600/10 border border-blue-500/20 rounded-xl p-4">
                <p className="text-xs text-blue-300/80">Your 30-day content plan tells your agents exactly what to post, when, and on which platform — without needing Draper to plan each day. You can skip this and set it up later in Content Studio.</p>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Playbook Industry</label>
                <div className="flex flex-wrap gap-2">
                  {PLAYBOOK_INDUSTRIES.map(ind => (
                    <button key={ind} data-testid={`playbook-industry-${ind.toLowerCase().replace(/[\s\/]/g, "-")}`} onClick={() => setPlaybookIndustry(ind)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${playbookIndustry === ind ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}>{ind}</button>
                  ))}
                </div>
                {playbookIndustry === "Other" && (
                  <input type="text" data-testid="input-playbook-other-industry" className="mt-2 w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50" placeholder="Describe your industry..." value={playbookOtherIndustry} onChange={e => setPlaybookOtherIndustry(e.target.value)} />
                )}
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Posting Frequency</label>
                <div className="flex gap-2">
                  {["Daily", "3-5x/week", "1-2x/week"].map(f => (
                    <button key={f} data-testid={`playbook-freq-${f.replace(/[\/x]/g, "")}`} onClick={() => setPlaybookFreq(f)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${playbookFreq === f ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}>{f}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Playbook Goals</label>
                <div className="flex flex-wrap gap-2">
                  {["Grow brand awareness", "Generate leads", "Drive website traffic", "Increase bookings", "Build community"].map(g => (
                    <button key={g} onClick={() => setPlaybookGoals(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g])} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${playbookGoals.includes(g) ? "bg-blue-600/20 text-blue-400 border border-blue-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}>{g}</button>
                  ))}
                </div>
              </div>

              {!playbookIndustry && !(playbookAction !== "none" && playbookResult) && (
                <p className="text-xs text-amber-400/80 font-medium" data-testid="hint-select-industry">Select an industry above to enable playbook options</p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="button-use-template"
                  onClick={handlePlaybookTemplate}
                  disabled={playbookLoading || !!(playbookAction !== "none" && playbookResult) || (!playbookIndustry && playbookIndustry !== "Other")}
                  className={`flex flex-col items-center gap-2 px-4 py-4 rounded-xl bg-emerald-600/10 border border-emerald-500/20 hover:bg-emerald-600/20 transition-all disabled:opacity-50 ${!playbookIndustry ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <BookOpen size={20} className="text-emerald-400" />
                  <span className="text-sm font-bold text-emerald-400">Use Industry Template</span>
                  <span className="text-xs text-muted-foreground/60 text-center">Instant — no AI cost</span>
                </button>
                <button
                  data-testid="button-generate-playbook"
                  onClick={() => {
                    const industryToUse = playbookIndustry === "Other" ? playbookOtherIndustry : playbookIndustry;
                    if (!industryToUse) {
                      toast({ title: "Select an industry", description: "Pick an industry first.", variant: "destructive" });
                      return;
                    }
                    setPlaybookAction("generate");
                    setPlaybookResult({ pendingGeneration: true, industry: industryToUse });
                    toast({ title: "Playbook queued", description: "Custom plan will be generated when you launch (~$0.01)." });
                  }}
                  disabled={playbookLoading || !!(playbookAction !== "none" && playbookResult) || (!playbookIndustry && playbookIndustry !== "Other")}
                  className={`flex flex-col items-center gap-2 px-4 py-4 rounded-xl bg-purple-600/10 border border-purple-500/20 hover:bg-purple-600/20 transition-all disabled:opacity-50 ${!playbookIndustry ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <Sparkles size={20} className="text-purple-400" />
                  <span className="text-sm font-bold text-purple-400">Generate Custom Playbook</span>
                  <span className="text-xs text-muted-foreground/60 text-center">AI-generated on launch (~$0.01)</span>
                </button>
              </div>

              {playbookAction === "template" && playbookResult && (
                <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl" data-testid="template-loaded-success">
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-emerald-400">Template loaded!</p>
                    <p className="text-xs text-muted-foreground/70">30-day {playbookResult.industry} playbook is ready. It will be applied when you launch.</p>
                  </div>
                </div>
              )}

              {playbookAction === "generate" && playbookResult?.pendingGeneration && (
                <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl" data-testid="generate-queued-success">
                  <Sparkles size={16} className="text-purple-400 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-purple-400">Custom playbook queued!</p>
                    <p className="text-xs text-muted-foreground/70">Your AI-generated 30-day plan for <span className="text-purple-400">{playbookResult.industry}</span> will be created when you launch. (~$0.01)</p>
                  </div>
                </div>
              )}

              <button
                data-testid="button-skip-playbook"
                onClick={() => setStep(s => s + 1)}
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground underline underline-offset-2 block text-center w-full"
              >
                Skip for now — set up playbook later in Content Studio
              </button>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6 text-center py-6">
              {deployPhase !== "idle" ? (
                <div className="space-y-6" data-testid="deploy-progress">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto shadow-lg shadow-blue-900/40">
                    {deployPhase === "done" ? (
                      <CheckCircle2 size={28} className="text-foreground" />
                    ) : (
                      <Loader2 size={28} className="text-foreground animate-spin" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-display font-bold text-foreground mb-2">
                      {deployPhase === "done" ? "All Set!" : "Deploying Your Agents"}
                    </h3>
                    <p className="text-muted-foreground text-sm">{deployPhaseLabels[deployPhase]}</p>
                  </div>
                  <div className="max-w-xs mx-auto space-y-2">
                    {(["creating", "branding", "analyzing", "calendar", "done"] as const).map((phase) => {
                      const labels: Record<string, string> = {
                        creating: "Creating workspace",
                        branding: "Extracting brand assets",
                        analyzing: "AI market analysis",
                        calendar: "Generating content calendar",
                        done: "Ready to go",
                      };
                      const phases = ["creating", "branding", "analyzing", "calendar", "done"];
                      const currentIdx = phases.indexOf(deployPhase);
                      const phaseIdx = phases.indexOf(phase);
                      const isComplete = phaseIdx < currentIdx;
                      const isCurrent = phaseIdx === currentIdx;
                      return (
                        <div key={phase} className="flex items-center gap-3 text-left">
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${
                            isComplete ? "bg-emerald-500/20 text-emerald-400"
                            : isCurrent ? "bg-blue-600/20 text-blue-400"
                            : "bg-card/50 text-muted-foreground/40"
                          }`}>
                            {isComplete ? <CheckCircle2 size={12} /> : isCurrent ? <Loader2 size={12} className="animate-spin" /> : <div className="w-1.5 h-1.5 rounded-full bg-current" />}
                          </div>
                          <span className={`text-xs font-medium ${isComplete ? "text-emerald-400" : isCurrent ? "text-foreground/70" : "text-muted-foreground/50"}`}>
                            {labels[phase]}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {deployPhase === "done" && !pilotMode && (
                    <div className="mt-2 text-center space-y-3" data-testid="section-post-deploy-pricing">
                      {!showPricing ? (
                        <button
                          onClick={() => setShowPricing(true)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-blue-900/30"
                          data-testid="button-view-plans"
                        >
                          <Sparkles size={14} />
                          View Plans &amp; Upgrade
                        </button>
                      ) : (
                        <div className="space-y-3" data-testid="pricing-cards-container">
                          <p className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-wider">Choose a plan to unlock full power</p>
                          {packages && packages.length > 0 ? (
                            <div className="grid grid-cols-1 gap-3 text-left max-w-sm mx-auto">
                              {packages.map((pkg: any) => (
                                <div
                                  key={pkg.id}
                                  className="bg-card/60 border border-border rounded-xl p-4 space-y-2"
                                  data-testid={`pricing-card-${pkg.id}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <p className="text-sm font-bold text-foreground">{pkg.name}</p>
                                      {pkg.description && <p className="text-xs text-muted-foreground/70">{pkg.description}</p>}
                                    </div>
                                    <div className="text-right shrink-0">
                                      <p className="text-lg font-bold text-foreground">${pkg.priceMonthly ?? "—"}<span className="text-xs font-normal text-muted-foreground">/mo</span></p>
                                    </div>
                                  </div>
                                  {pkg.features && (
                                    <ul className="space-y-1">
                                      {(Array.isArray(pkg.features) ? pkg.features : []).slice(0, 4).map((f: string, i: number) => (
                                        <li key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
                                          <CheckCircle2 size={10} className="text-emerald-400 shrink-0" />
                                          {f}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  <button
                                    onClick={async () => {
                                      try {
                                        setCheckingOut(String(pkg.id));
                                        const res = await apiRequest("POST", "/api/stripe/checkout", { packageId: pkg.id, successUrl: `${window.location.origin}/dashboard`, cancelUrl: window.location.href });
                                        const data = await res.json();
                                        if (data.url) window.location.href = data.url;
                                      } catch {
                                        toast({ title: "Checkout error", description: "Could not start checkout. Please try again.", variant: "destructive" });
                                      } finally {
                                        setCheckingOut(null);
                                      }
                                    }}
                                    disabled={checkingOut === String(pkg.id)}
                                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50"
                                    data-testid={`button-checkout-${pkg.id}`}
                                  >
                                    {checkingOut === String(pkg.id) ? <Loader2 size={12} className="animate-spin" /> : null}
                                    Get Started
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="py-4 text-center">
                              <Loader2 size={18} className="animate-spin text-muted-foreground mx-auto" />
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground/40 text-center">
                            Or{" "}
                            <a href="/dashboard" className="text-blue-400 hover:underline" data-testid="link-skip-to-dashboard">
                              go to dashboard &rarr;
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center mx-auto shadow-lg shadow-blue-900/40">
                    <Sparkles size={28} className="text-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-display font-bold text-foreground mb-2">You're All Set</h3>
                    <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                      Draper will analyze <span className="text-foreground/70 font-semibold">{form.name}</span> and build a personalized multi-platform marketing strategy.
                    </p>
                  </div>
                  <div className="bg-blue-500/[0.06] border border-blue-500/[0.1] rounded-xl p-4 text-left max-w-sm mx-auto mb-3" data-testid="what-happens-next">
                    <h4 className="text-xs font-bold text-foreground/80 mb-2 flex items-center gap-1.5">
                      <Sparkles size={12} className="text-blue-400" />
                      What happens next
                    </h4>
                    <div className="space-y-1.5">
                      <p className="text-[11px] text-muted-foreground/70 flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-blue-400 shrink-0" />
                        Draper will build your strategy
                      </p>
                      <p className="text-[11px] text-muted-foreground/70 flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-purple-400 shrink-0" />
                        Peggy will fill your content calendar
                      </p>
                      <p className="text-[11px] text-muted-foreground/70 flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                        Pete will draft ad campaigns
                      </p>
                    </div>
                  </div>
                  <div className="bg-muted/30 border border-border rounded-xl p-4 text-left max-w-sm mx-auto" data-testid="launch-summary">
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Business</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-business">{form.name || "Not set"}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Industry</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-industry">{form.industry || "Not set"}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Voice</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-voice">{form.brandVoice}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Channels</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-channels">{selectedChannelLabels.join(", ")}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Goals</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-goals">{form.goals.length} selected</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Posting</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-frequency">{form.postingFrequency}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Personality</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-personality">{form.personality}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground/70">Content Styles</span>
                        <span className="text-foreground/70 font-medium" data-testid="summary-content-styles">{form.contentStyles.length} selected</span>
                      </div>
                    </div>
                    {(manualBrand.logoUrl || brandKit.primaryColor !== "#2563eb") && (
                      <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid="brand-kit-summary">
                        <span className="text-xs text-muted-foreground/70 font-bold uppercase tracking-widest">Brand Kit</span>
                        <div className="flex items-center gap-3">
                          {manualBrand.logoUrl && !isBareDomainUrl(manualBrand.logoUrl) && (
                            <div className="flex items-center gap-2">
                              <img src={manualBrand.logoUrl} alt="Logo" className="w-6 h-6 rounded object-contain bg-white/5 border border-border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                              <span className="text-xs text-emerald-400">Logo</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            {[brandKit.primaryColor, brandKit.secondaryColor, brandKit.accentColor].filter(c => c && c !== "#2563eb" && c !== "#1e40af" && c !== "#f59e0b").map(c => (
                              <div key={c} className="w-4 h-4 rounded-sm border border-border" style={{ backgroundColor: c }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {brandAssets && (brandAssets.logoUrl || brandAssets.brandColors.length > 0) && (
                      <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid="brand-assets-preview">
                        <span className="text-xs text-muted-foreground/70 font-bold uppercase tracking-widest">Extracted Brand Assets</span>
                        {brandAssets.logoUrl && (
                          <div className="flex items-center gap-3" data-testid="brand-logo-preview">
                            <img
                              src={brandAssets.logoUrl}
                              alt="Brand logo"
                              className="w-8 h-8 rounded-md object-contain bg-white/10"
                            />
                            <span className="text-xs text-foreground/70 font-medium">Logo detected</span>
                          </div>
                        )}
                        {brandAssets.brandColors.length > 0 && (
                          <div className="flex items-center gap-2" data-testid="brand-colors-preview">
                            <span className="text-xs text-muted-foreground/70">Colors</span>
                            <div className="flex items-center gap-1">
                              {brandAssets.brandColors.map((color, i) => (
                                <div
                                  key={i}
                                  className="w-5 h-5 rounded-md border border-border"
                                  style={{ backgroundColor: color }}
                                  title={color}
                                  data-testid={`brand-color-swatch-${i}`}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-border">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || deployMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground/70 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              data-testid="button-onboarding-back"
            >
              <ArrowLeft size={14} />
              Back
            </button>

            {step < 6 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canAdvance()}
                className="flex items-center gap-1.5 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="button-onboarding-next"
              >
                Continue
                <ArrowRight size={14} />
              </button>
            ) : deployPhase === "done" ? (
              <a
                href="/dashboard"
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-900/30"
                data-testid="link-go-to-dashboard"
              >
                <CheckCircle2 size={16} />
                Go to Dashboard
              </a>
            ) : (
              <button
                onClick={() => deployMutation.mutate()}
                disabled={deployMutation.isPending}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-blue-900/30"
                data-testid="button-launch-workspace"
              >
                {deployMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Sparkles size={16} />
                )}
                Launch My Marketing
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
